/**
 * Variant Media Grouping
 * -----------------------
 * Shows every product image tagged "#ColorName" in its alt text when the
 * matching color is selected, instead of the single image Shopify allows per
 * variant. Images with no "#" tag are shared and stay visible for every color.
 *
 * All product media stays in the DOM (see main-product.liquid: the native
 * variant_media_display Liquid filtering is disabled when grouping is ON).
 * This script filters in place by showing/hiding items and keeps the existing
 * Swiper sliders/thumbs intact — it never destroys/recreates them.
 *
 * The color group for each media item is read from the rendered <img alt>,
 * which is the single source of truth. Matching is handleized and
 * case-insensitive; only the part after the last "#" is the group key.
 *
 * Public API: window.updateVariantMediaDisplay(sectionId, variant)
 */
(function () {
  var HIDDEN_ITEM_CLASS = "product__media-item--variant-hidden";
  var HIDDEN_SUBITEM_CLASS = "product__media-subitem--variant-hidden";

  // Mirrors Shopify's `handle` filter closely enough for group matching.
  function handleize(str) {
    return String(str == null ? "" : str)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Resolve the color group for a media item from its <img alt>.
  // Empty string = shared (no "#" tag) and always visible.
  function groupForItem(item) {
    var img = item.querySelector("img");
    var alt = img ? img.getAttribute("alt") || "" : "";
    var group = "";
    if (alt.indexOf("#") !== -1) {
      group = handleize(alt.split("#").pop());
    }
    // Cache for debugging / later reads; recomputed each pass so it stays fresh.
    item.dataset.mediaGroup = group;
    return group;
  }

  function selectedGroup(variant, optionPosition) {
    if (!variant || !variant.options || !optionPosition) return "";
    return handleize(variant.options[optionPosition - 1] || "");
  }

  // Toggle visibility (and swiper-slide membership) for every media item in a root.
  function applyVisibility(root, variant) {
    var optionPosition = parseInt(root.dataset.groupOptionPosition || "0", 10);
    if (!optionPosition) return;

    var selected = selectedGroup(variant, optionPosition);
    var items = root.querySelectorAll("[data-media-id], [data-media-sub-id]");

    items.forEach(function (item) {
      var isSub = item.hasAttribute("data-media-sub-id");
      var group = groupForItem(item);
      var visible = group === "" || group === selected;

      item.classList.toggle(
        isSub ? HIDDEN_SUBITEM_CLASS : HIDDEN_ITEM_CLASS,
        !visible
      );

      // Hidden items must NOT be Swiper slides, but must stay in the wrapper.
      // Only toggle swiper-slide for direct children of a .swiper-wrapper.
      var parent = item.parentElement;
      if (parent && parent.classList.contains("swiper-wrapper")) {
        item.classList.toggle("swiper-slide", visible);
      }
    });
  }

  // Refresh a single Swiper without destroying it, preserving its direction.
  function refreshSwiper(sliderEl, variant, sectionId) {
    var swiper = sliderEl.swiper;
    if (!swiper) return;

    var direction = swiper.params.direction;

    try {
      swiper.updateSize();
      swiper.updateSlides();
      swiper.updateProgress();
      swiper.updateSlidesClasses();
      swiper.update();
    } catch (e) {
      /* Swiper mid-transition; safe to ignore */
    }

    // Never let an update flip a vertical thumbs column into a horizontal strip
    // (or vice versa). Restore the direction that was live before the update.
    if (
      swiper.params.direction !== direction &&
      typeof swiper.changeDirection === "function"
    ) {
      swiper.changeDirection(direction, false);
    }

    // Slide to the selected variant's featured media if it is visible,
    // otherwise to the first visible slide.
    var target = 0;
    if (variant && variant.featured_media) {
      var featuredId = sectionId + "-" + variant.featured_media.id;
      for (var i = 0; i < swiper.slides.length; i++) {
        var slide = swiper.slides[i];
        if (
          slide.getAttribute("data-media-id") === featuredId ||
          slide.getAttribute("data-media-sub-id") === featuredId
        ) {
          target = i;
          break;
        }
      }
    }

    try {
      swiper.slideTo(target, 0);
    } catch (e) {
      /* no-op */
    }
  }

  // Hide the thumbnail strip entirely when a color has one (or zero) images,
  // so a lone thumbnail doesn't linger next to the main image.
  function updateThumbsVisibility(root) {
    var sublist = root.querySelector(".js-media-sublist");
    if (!sublist) return;

    var visibleThumbs = sublist.querySelectorAll(
      ".product__media-subitem:not(." + HIDDEN_SUBITEM_CLASS + ")"
    );
    sublist.style.display = visibleThumbs.length <= 1 ? "none" : "";
  }

  function refreshSliders(root, variant, sectionId) {
    var sliders = root.querySelectorAll(
      ".js-media-list, .global-variant-js-media-list, .js-media-list-horizontal, .quick-js-media-list, .js-media-sublist"
    );
    sliders.forEach(function (sliderEl) {
      refreshSwiper(sliderEl, variant, sectionId);
    });
    updateThumbsVisibility(root);
  }

  /**
   * Public entry point. Filters the gallery of `sectionId` to the color group of
   * `variant`, then refreshes the existing sliders in place.
   */
  window.updateVariantMediaDisplay = function (sectionId, variant) {
    var roots = document.querySelectorAll(
      '[data-alt-media-grouping="true"][data-section="product-media-' +
        sectionId +
        '"]'
    );
    if (!roots.length) return;

    roots.forEach(function (root) {
      applyVisibility(root, variant);
      refreshSliders(root, variant, sectionId);
    });

    // Let autoHeight sliders and any height calculators recompute.
    try {
      window.dispatchEvent(new Event("resize"));
    } catch (e) {
      /* no-op */
    }
  };

  function getSelectedVariant(sectionId) {
    var section = document.getElementById("shopify-section-" + sectionId);
    var scope = section || document;
    var el = scope.querySelector("[data-selected-variant]");
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-alt-media-grouping="true"]');
    roots.forEach(function (root) {
      var sectionAttr = root.getAttribute("data-section") || "";
      var sectionId = sectionAttr.replace("product-media-", "");
      if (!sectionId) return;

      var variant = getSelectedVariant(sectionId);
      // Apply visibility first (strips swiper-slide from hidden items) so the
      // Swiper init that follows only counts the visible slides.
      applyVisibility(root, variant);
      refreshSliders(root, variant, sectionId);
    });
  }

  // This script is loaded (deferred) BEFORE the slider-init scripts, so running
  // now filters the DOM before Swiper is created — no flash, no broken slides.
  initAll();

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);
})();
